import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { findCurrentLoan } from 'store/actions/customer-actions';

// Components
import { Button, Container, Typography } from 'lib-ui-react';
import Loading from 'components/features/loading/loading';
import ErrorNotification from '../../../components/features/error-notification/error-notification';

// General
import { goToLoanRoute } from '../../../navigation/application-routes';
import routes from 'resources/routes.json';
import { analyseBankInfo } from 'utils/helper/bank-helper';

// Utils
import { BANK_LIST } from '../../../utils/enumerator/enum-bank-code';
import { updateBankInformation, UPDATE_BANK_INFORMATION_SUCCESS } from '../../../store/actions/application-actions';
import { isMobile } from '../../../utils/helper/page-behavior-helper';
import { showMenu, showFooter } from "store/actions/util-actions";
import styles from './styles.module.scss';

function BankInfoUpdateNew() {
  const device = useSelector((state) => state?.customer?.device);
  const loan = useSelector((state) => state?.personalLending?.loan);
  const dispatch = useDispatch();

  const [edit, setEdit] = useState(false);
  const [selectedBank, setSelectedBank] = useState({});
  const [bankCode, setBankCode] = useState(null);
  const [bankBranch, setBankBranch] = useState(null);
  const [bankAccountNumber, setBankAccountNumber] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState(null);
  const dueDay = null;

  const footerStyle = isMobile() ? { position: 'fixed', bottom: 0, left: 0 } : {};

  useEffect(() => {
    dispatch(showMenu(false));
    dispatch(showFooter(false));
  }, [dispatch]);

  function onChangeBankCode(value) {
    setBankCode(value);
    setBankBranch(null);
    setBankAccountNumber(null);
    setErrors([]);
  }

  function onChangeBankBranch(e) {
    setBankBranch(e.target.value.replace(/[^\d]/g, '').replace(/(\d{1,4})/, '$1'));
  }

  function onChangeBankAccountNumber(e) {
    setBankAccountNumber(e.target.value.replace(/[^\d]/g, '').replace(/(\d{1,11})(\d{1})/, '$1-$2'));
  }

  function updateBankInformationClick() {
    setLoading(true);
    setErrors([]);

    if (bankAccountNumber === null && bankBranch === null) {
      setErrors(['Favor, preencher os campos de agência e conta para continuar!']);
      setLoading(false);
    } else {
      if (analyseBankInfo(bankCode, bankBranch, bankAccountNumber.split('-')[0], bankAccountNumber.split('-')[1])) {
        dispatch(
          updateBankInformation(
            bankCode,
            bankBranch,
            bankAccountNumber.split('-')[0],
            bankAccountNumber.split('-')[1],
            dueDay,
            device,
          ),
        ).then((response) => {
          if (response.type === UPDATE_BANK_INFORMATION_SUCCESS) {
            dispatch(findCurrentLoan(device));
            goToLoanRoute(loan, routes.CUSTOMER_UPDATE_BANK_ACCOUNT, routes.LOAN_APPLICATION_CONTRACT);
          } else {
            setErrors(['Erro ao atualizar dados. Verifique e tente novamente.']);
          }
          setLoading(false);
        });
      } else {
        setErrors(['Conta invalida, verifique e tente novamente.']);
        setLoading(false);
      }
    }
  }

  function handleSelectedBank(bank) {
    setEdit(!edit);
    onChangeBankCode(bank.code);
    setSelectedBank(bank);
  }

  function goBack() {
    goToLoanRoute(loan, routes.CUSTOMER_UPDATE_BANK_ACCOUNT, routes.LOAN_APPLICATION_CONTRACT);
  }

  if (loading) {
    return (
      <>
        <Loading title="Aguarde um momento, por favor..." />
      </>
    );
  }

  return (
    <div className={styles.pageContainer}>
      <Container align="center">
        <div className={styles.centeredContainer}>
          <img src="/image/simple-logo.png" alt="Logo Supersim" width="38.9"/>
        </div>
        <Container maxWidth="480px">
          {!edit && (
            <>
              <Container align="center" spacing={64} padding={16} height="100vh">
                <Container spacing={8}>
                  <Typography type="title" size="medium" fontWeight={600} lineHeight="28px" color="#2A2B35">
                    Qual é a sua conta bancária preferencial?
                  </Typography>
                  <Typography size="small" fontWeight={400} lineHeight="20px" color="#757575">
                    Escolha <b>uma opção abaixo</b> para receber o seu empréstimo.
                  </Typography>
                </Container>
                <div
                  style={{
                    display: 'grid',
                    gap: '12px',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    padding: '0 24px',
                  }}
                >
                  {BANK_LIST.map((item) => (
                    <Button
                      key={item.code}
                      outlined
                      borderColor="#EEEEEE"
                      width="64px"
                      height="64px"
                      startIcon={item.logo}
                      onClick={() => handleSelectedBank(item)}
                    />
                  ))}
                </div>

                <Container
                  align="center"
                  justify="center"
                  padding="16px"
                  spacing={8}
                  background="#fafafa"
                  width="100%"
                  style={footerStyle}
                >
                  <Button ghost onClick={goBack}>
                    Não possuo nenhum destes bancos
                  </Button>
                </Container>
              </Container>
            </>
          )}
          {edit && (
            <>
              <div id="form-area">
                <ErrorNotification errors={errors}/>

                {/* Title */}
                <Container align="center" spacing={32} padding={16} height="100vh">
                  <Typography type="title" size="medium">
                    Insira os dados da sua conta
                  </Typography>

                  <Container spacing={16}>
                    <Button
                      id="bankCode"
                      outlined
                      borderColor="#FBA832"
                      width="64px"
                      height="64px"
                      margin="0"
                      startIcon={selectedBank.logo}
                      onClick={() => setEdit(!edit)}
                      style={{
                        border: '1px solid',
                        boxShadow: '0px 2px 0px 0px #FBA832',
                      }}
                    />

                    <Container spacing={16}>
                      <Container spacing={4}>
                        <Typography size="small" fontWeight={700} lineHeight="16px" color="#2A2B35" margin="0 4px">
                          Número da Agência (sem dígito)
                        </Typography>
                        <input
                          id="bankBranch"
                          className={styles.bankInput}
                          placeholder="ex: 0000"
                          pattern="\d{1,4}"
                          maxLength={4}
                          value={bankBranch}
                          onChange={onChangeBankBranch}
                        />
                      </Container>

                      <Container spacing={4}>
                        <Typography size="small" fontWeight={700} lineHeight="16px" color="#2A2B35" margin="0 4px">
                          Número da Conta (com dígito)
                        </Typography>
                        <input
                          id="bankAccountNumber"
                          className={styles.bankInput}
                          placeholder="ex: 00000-0"
                          pattern="\d\[^-]"
                          maxLength={13}
                          value={bankAccountNumber}
                          onChange={onChangeBankAccountNumber}
                        />
                      </Container>
                    </Container>
                  </Container>
                  <Container
                    align="center"
                    justify="center"
                    padding="16px"
                    spacing={8}
                    background="#fafafa"
                    width="100%"
                    style={footerStyle}
                  >
                    <Button onClick={updateBankInformationClick} disabled={loading} loading={loading} margin="0">
                      Atualizar conta
                    </Button>
                  </Container>
                </Container>
              </div>
            </>
          )}
        </Container>
      </Container>
    </div>
  );
}

export default BankInfoUpdateNew;
